import React, { PropsWithChildren, useCallback, useMemo, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import {
  DraftActionsType,
  DraftContextType,
  DraftContext,
} from 'new/components/flows/types/genai_rag/context/Draft/DraftContext';
import { useIsLoading } from 'new/hooks/adapters/useIsLoading';
import usePipelinesAdapter from 'new/state/pipelines/adapter/usePipelinesAdapter';
import { draftEventTypes } from './useDraftContext';
import { usePreviousPath } from 'new/hooks/usePreviousPath';
import { getCurrentProjectId } from 'js/reducers/app/selectors';
import { ROUTE_CONNECT } from 'new/constants/pathnames';
import { clearSelectedFlow } from 'js/actions';
import { clearCurrentProjectId } from 'js/actions/currentProject';

function validateDraftAction(draftAction: DraftActionsType) {
  if (!Object.values(draftAction).length) return false;
  return Object.values(draftAction).reduce((valid, action) => {
    if (valid) {
      if (!action.actions?.length) return false;
      if (action.args?.length && action.args.some((item) => !Array.isArray(item))) return false;
      return true;
    }
    return valid;
  }, true);
}

export function DraftContextProvider(props: PropsWithChildren<any>) {
  const { flowId } = props;
  const [draftActions, setDraftActions] = useState<DraftActionsType>({});
  const [draftState, setDraftState] = useState({});
  const listeners = useRef([]);
  const { publishPipeline } = usePipelinesAdapter();
  const [showDismissConfirmation, setShowDismissConfirmation] = useState(false);
  const [showPublishConfirmation, setShowPublishConfirmation] = useState(false);
  const history = useHistory();
  const previousPath = usePreviousPath();
  const currentProjectId = useSelector(getCurrentProjectId);
  const dispatch = useDispatch();

  const loading = useIsLoading(
    ...Object.values(draftActions).reduce((types, action) => {
      types.push(...action.requestTypes);
      return types;
    }, []),
  );

  const setDraftActionsLocal = useCallback((draftAction: DraftActionsType) => {
    if (validateDraftAction(draftAction)) {
      setDraftActions((drafState) => ({ ...drafState, ...draftAction }));
    } else {
      throw new Error(
        'You should set an "actions" property in your DraftAction. \nYou should set each item in the "args" property as an array of arguments to be spread',
      );
    }
  }, []);

  const execute = async () => {
    const requests = Object.values(draftActions).reduce((reqs, draftAction) => {
      if (draftAction.sync) {
        async function syncReq() {
          for (let i = 0; i < draftAction.actions.length; i++) {
            await draftAction.actions[i](...draftAction.args[i]);
          }
        }
        reqs.push(syncReq());
      } else {
        draftAction.actions.forEach((action, index) => {
          const args = draftAction.args[index];
          if (args) {
            reqs.push(action(...args));
          } else {
            reqs.push(action());
          }
        });
      }
      return reqs;
    }, []);
    await Promise.all(requests);
    await Promise.all(listeners.current.map((listener) => listener()));
    await publishPipeline(flowId);
    setDraftActions({});
    setDraftState({});
  };

  const addExecuteListener = (listener: (data: any) => void) => {
    if (!listeners.current.includes(listener)) {
      listeners.current.push(listener);
    }
  };

  const removeExecuteListener = (listener: (data: any) => void) => {
    if (listeners.current.includes(listener)) {
      const indexToRemove = listeners.current.indexOf(listener);
      listeners.current.splice(indexToRemove, 1);
    }
  };

  const handleEvent = (eventType: keyof typeof draftEventTypes) => {
    switch (eventType) {
      case draftEventTypes.EXIT:
        setShowDismissConfirmation(true);
        break;
      case draftEventTypes.DISMISS:
        setShowDismissConfirmation(false);

        if (currentProjectId) {
          // Clear any selected flow state before redirecting
          dispatch(clearSelectedFlow() as any);
          history.replace(`${ROUTE_CONNECT}/projects/${currentProjectId}`);
          // Clear the project ID from Redux after redirecting
          dispatch(clearCurrentProjectId() as any);
        } else if (previousPath) {
          history.goBack();
        } else {
          history.push('/');
        }
        break;
      case draftEventTypes.PUBLISH:
        setShowPublishConfirmation(true);
        break;
      case draftEventTypes.PUBLISH_CONFIRM:
        setShowPublishConfirmation(false);
        execute();
        break;
    }
  };

  const providerValue = useMemo<DraftContextType>(() => {
    return {
      loading,
      isEdit: true,
      draftActions: draftActions,
      setDraftActions: setDraftActionsLocal,
      setDraftState,
      addExecuteListener,
      removeExecuteListener,
      draftState,
      execute,
      handleEvent,
      showDismissConfirmation,
      setShowDismissConfirmation,
      showPublishConfirmation,
      setShowPublishConfirmation,
    };
  }, [draftActions, loading, draftState, showDismissConfirmation, showPublishConfirmation]);

  return <DraftContext.Provider value={providerValue}>{props.children}</DraftContext.Provider>;
}
